import { isAxiosError, type AxiosResponse } from 'axios'
import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { type IRowDefault } from 'shared/ui/TableNew'
import { TableStore } from 'shared/ui/TableNew/store/tableStore'
import { RequestLoadData } from 'shared/api/lib'
import { logger } from 'shared/lib'
import { type IParamsClient } from 'entities/Clients/api/types'
import { tableLimitStore } from 'entities/TableLimit'
import { type IPaginationSourceType } from 'entities/Users/api/types'

export type IClientsTabStoreConfig<
  TItem extends { id: string | number },
  TResponse extends { data: TItem[]; total: number },
  TRow extends IRowDefault = IRowDefault
> = {
  table: {
    key: IPaginationSourceType
    element: string
    useColumnsCache?: boolean
    sortBy?: string
  }
  request: (params: IParamsClient) => Promise<AxiosResponse<TResponse>>
  makeRow: (item: TItem) => TRow
}

export class ClientsTabStore<
  TItem extends { id: string | number },
  TResponse extends { data: TItem[]; total: number },
  TRow extends IRowDefault = IRowDefault
> {
  tableStore: TableStore<TRow>
  private _request: (params: IParamsClient) => Promise<AxiosResponse<TResponse>>
  private _makeRow: (item: TItem) => TRow

  private _itemsMap: Map<TItem['id'], TItem> = new Map()
  private _scrollToTopTrigger = ''
  private _disposeParamsGetItems: IReactionDisposer | null = null
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _hasRetriedAtMinLimit = false
  private _isRetrying = false
  private _tableKey: IPaginationSourceType

  constructor(config: IClientsTabStoreConfig<TItem, TResponse, TRow>) {
    this._tableKey = config.table.key

    this.tableStore = new TableStore<TRow>({
      key: config.table.key,
      element: config.table.element,
      useColumnsCache: config.table.useColumnsCache,
      page: 1,
      limit: 10,
      getLimit: () => tableLimitStore.getLimit(this._tableKey),
      setLimit: (value) => tableLimitStore.setLimit(this._tableKey, value),
      sortBy: config.table.sortBy,
      sortOrder: 'desc',
      onSort: () => {
        this.tableStore.setPage(1)
      },
    })

    this._request = config.request
    this._makeRow = config.makeRow

    makeAutoObservable(this)
  }

  init = () => {
    void this.loadData()
    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this.tableStore.setLoading(true)
        this.tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async () => {
      runInAction(() => {
        this.tableStore.setLoading(true)
      })

      try {
        const { data } = await this._request(this.paramsGetItems)

        runInAction(() => {
          this._itemsMap.clear()
          data.data.forEach((item) => {
            this._itemsMap.set(item.id, item)
          })

          this.tableStore.setTotal(data.total)
          this._scrollToTopTrigger = nanoid()
          this._hasRetriedAtMinLimit = false
        })
      } catch (error) {
        const isTimeout =
          isAxiosError(error) && (error.response?.status === 408 || error.response?.status === 504)
        if (isTimeout) {
          logger.warn(
            `TABLE: ${this._tableKey} Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.`
          )
          const currentLimit = this.tableStore.limit
          const newLimit = tableLimitStore.reduceLimit(this._tableKey, currentLimit)
          if (newLimit !== currentLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = false
              this._isRetrying = true
            })
            this.tableStore.setLimit(newLimit)
          } else if (!this._hasRetriedAtMinLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = true
              this._isRetrying = true
            })
            this._requestLoadData.debounceLoadData()
          }
        } else {
          throw error
        }
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        if (!this._isRetrying) {
          this.tableStore.setLoading(false)
        }
        this._isRetrying = false
      })
    },
  })

  loadData = async () => {
    return this._requestLoadData.loadData()
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get itemsRows() {
    return this.items.map((item) => this._makeRow(item))
  }

  get paramsWithoutPage(): Omit<IParamsClient, 'page'> {
    return {
      length: this.tableStore.limit,
      search: this.tableStore.search || undefined,
      sortBy: this.tableStore.sortBy || undefined,
      sortOrder: this.tableStore.sortOrder,
    }
  }

  get paramsGetItems(): IParamsClient {
    return {
      page: this.tableStore.page,
      ...this.paramsWithoutPage,
    }
  }

  get loading() {
    return this.tableStore.loading
  }

  get scrollToTopTrigger() {
    return this._scrollToTopTrigger
  }

  resetPaginationPage = () => {
    this.tableStore.setPage(1)
  }

  onDeleteItem = (id: TItem['id']) => {
    this._itemsMap.delete(id)
  }

  dispose = () => {
    this.resetPaginationPage()
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadData.dispose()
  }
}
